import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  BasePagination,
  CustomError,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { useQuery } from "react-query";
import { changeSortOrderAntd } from "components/atoms/table/AntdTable";
import { TableColumnsType } from "antd";
import { ReactComponent as Key } from "assets/icons/key.svg";
import BigButton from "components/atoms/buttons/BigButton";
import TableButton from "components/atoms/buttons/TableButton";
import LyvePresenter from "./LyvePresenter";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import * as mediaQuery from "components/MediaQuery";
import {
  GetLyveListResponse,
  LicenseDetailData,
  SubscriptionPageOutletContext,
  UpdateLyveStorageSizeResponse,
  SUBSCRIPTION_LOCATION,
  CUMULUS_TYPE,
} from "api/interfaces/subscription.interface";
import { convertMsToDays, getAccountTimeFormatDate } from "utils/timeUtil";
import {
  getLyveHistoryList,
  getLyveList,
  updateLyveStorageSize,
} from "api/subscriptionAPI";
import { useMenu } from "hook/useMenu";
import { SubscriptionKeyColumn } from "./list/SubscriptionKeyColumn";
import {
  calcStoragePercent,
  convertTBtoBytes,
  formatBytesAndUnits,
} from "utils/functions";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import * as s from "./LyveStyled";
import useStatusMutation from "hook/useStatusMutation";
import useApiError from "hook/useApiError";
import moment from "moment";

const initialQueryinfo: BasePagination = {
  ...baseQueryInfo,
  sortType: "expiredAt",
  sortDirection: SORT_DIRECTION.ASC,
};

const convertGBToTB = (size: number) => {
  const base = size % 1024 === 0 ? 1024 : 1000;
  return Number((size / base).toFixed(2));
};

export function LyveContainer(): JSX.Element {
  const location = useLocation();
  const { queryNavigate } = useMenu({});

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [pageInfo, setPageInfo] = useState<BasePagination>({
    ...initialQueryinfo,
  });

  const { onHeaderCell, onChangePage, setStatus, setButtons } =
    useOutletContext<SubscriptionPageOutletContext>();
  const [searchParams] = useSearchParams();
  const queryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...initialQueryinfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...{ type: get("type") ?? "" },
    };
  }, [searchParams, pageInfo]);
  const queryKey = useQueryKey(queryInfo);

  const isMobile = mediaQuery.useIsMobile();
  const [isAddModal, setIsAddModal] = useState<boolean>(false);
  const [isEditModal, setIsEditModal] = useState<boolean>(false);
  const [isLicenseModal, setIsLicenseModal] = useState<boolean>(false);
  const [isRemoveLicenseModal, setIsRemoveLicenseModal] =
    useState<boolean>(false);
  const [selectRow, setSelectRow] = useState<LicenseDetailData>();
  const type: CUMULUS_TYPE = useMemo(() => {
    return CUMULUS_TYPE.WASABI;
  }, []);
  const bucketName = useMemo(() => {
    return location.pathname
      .replace(
        SUBSCRIPTION_LOCATION.LYVE,
        ""
      )
      .slice(1);
  }, [location.pathname]);
  const [updatedKey, setUpdatedKey] = useState<string>("");

  const {
    handleErrorNotify,
    intlString,
    lang,
    createCustomError,
    notifySuccess,
  } = useApiError();
  const updateErrorMsg = intlString(lang.label.lyve.notify.update.fail);

  const initialData = () => {
    setPageInfo({...initialQueryinfo});
    setStatus({
      totalTitle: "Total",
      totalValue: 0,
    });
  };
  const onSuccessList = useCallback(
    (res: GetLyveListResponse) => {
      if (res.error !== 0 || !res.result) {
        initialData();
        return;
      }
      setPageInfo((query) => {
        return {
          ...query,
          ...res.page,
        };
      });
      setStatus({
        totalTitle: "Total",
        totalValue: res.page.total,
      });
    },
    [initialData]
  );

  const {
    refetch: refetchList,
    isLoading: isLoadingList,
    data: serverData,
  } = useQuery(
    ["lyve", queryKey, type],
    () =>
      getLyveList({
        provider: type,
        payload: {
          ...queryInfo,
          total: 0,
        },
      }),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !bucketName,
      onSuccess: onSuccessList,
      onError: (e: CustomError) => {
        initialData();
      },
    }
  );
  const {
    refetch: refetchHistory,
    isLoading: isLoadingHistory,
    data: serverDataHistory,
  } = useQuery(
    ["lyveHistory", queryKey, bucketName, type],
    () =>
      getLyveHistoryList({
        payload: {
          ...queryInfo,
          total: 0,
        },
        bucketName: bucketName,
        provider: type,
      }),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!bucketName,
      onSuccess: onSuccessList,
      onError: (e: CustomError) => {
        initialData();
      },
    }
  );

  const data = useMemo(() => {
    const result = bucketName ? serverDataHistory?.result : serverData?.result;
    return result?.map((item: LicenseDetailData) => {
      return {
        ...item,
        key: item.subscription_key_id,
        leftDays: convertMsToDays(
          moment(item.expired_at, "UTC").toDate().getTime()
        ),
      };
    });
  }, [bucketName, serverData?.result, serverDataHistory?.result]);

  const refetch = useCallback(() => {
    if (bucketName) {
      refetchHistory();
    } else {
      refetchList();
    }
  }, [bucketName, refetchHistory, refetchList]);

  const onModal = useCallback(
    (type: string, row?: LicenseDetailData) => {
      if (type === "add") {
        if (isAddModal) {
          queryNavigate({
            to: location.pathname,
            queryInfo: {
              ...initialQueryinfo,
            },
          });
        }
        setIsAddModal(!isAddModal);
      }
      if (type === "edit") {
        setSelectRow(row);
        setIsEditModal(!isEditModal);
        setUpdatedKey("");
        if (!row && updatedKey !== "") {
          refetch();
        }
      }
      if (type === "license") {
        setIsLicenseModal(!isLicenseModal);
      }
      if (type === "remove") {
        setIsRemoveLicenseModal(!isRemoveLicenseModal);
      }
    },
    [
      isAddModal,
      isEditModal,
      isLicenseModal,
      isRemoveLicenseModal,
      location.pathname,
      queryNavigate,
      refetch,
      updatedKey,
    ]
  );
  const columns: TableColumnsType<LicenseDetailData> = [
    {
      title: "CPP #",
      key: "cppNumber",
      render: (value, row) => (
        <s.Column isActive={row.is_active || row.leftDays < 0}>
          {row.cpp_number}
        </s.Column>
      ),

      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "cppNumber"),
      onHeaderCell: onHeaderCell,
      width: 90,
    },
    {
      title: "Key",
      render: (value, row) => (
        <s.Column isActive={row.is_active || row.leftDays < 0}>
          <SubscriptionKeyColumn subscriptionKey={row.subscription_key} />
        </s.Column>
      ),

      responsive: !!bucketName ? undefined : ["md"],
    },
    {
      title: "Bucket Name",
      key: "bucketName",
      render: (value, row) => (
        <s.Column isActive={row.is_active || row.leftDays < 0}>
          {row.bucket_name}
        </s.Column>
      ),

      width: 240,
      hidden: !!bucketName,
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "bucketName"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Usage",
      key: "usageSize",
      render: (value, row) => {
        const usage = formatBytesAndUnits(row.usage_size, 1000);
        const storageToTB = convertGBToTB(row.storage_size);
        const totalSize = convertTBtoBytes(storageToTB, 1000);
        const percentage = calcStoragePercent(row.usage_size, totalSize);
        return (
          <s.Column
            isActive={row.is_active || row.leftDays < 0}
            percentage={percentage}
          >
            {row.usage_size ? `${usage.value} ${usage.unit}` : ""}
          </s.Column>
        );
      },
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "usageSize"),
      onHeaderCell: onHeaderCell,
      width: 80,
      responsive: ["md"],
    },
    {
      title: "Total",
      key: "storageSize",
      render: (value, row) => {
        return (
          <s.Column isActive={row.is_active || row.leftDays < 0}>
            {convertGBToTB(row.storage_size)}TB
          </s.Column>
        );
      },
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "storageSize"),
      onHeaderCell: onHeaderCell,
      width: 70,
      responsive: ["lg"],
    },
    {
      title: "Duration",
      key: "duration",
      render: (value, row) => (
        <s.Column isActive={row.is_active || row.leftDays < 0}>
          {row.duration} Year{row.duration > 1 ? "s" : ""}
        </s.Column>
      ),

      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "duration"),
      onHeaderCell: onHeaderCell,
      width: 80,
      responsive: ["xxl"],
    },
    {
      title: "Created By",
      key: "createdBy",
      render: (value, row) => (
        <s.Column isActive={row.is_active || row.leftDays < 0}>
          {row.created_by}
        </s.Column>
      ),

      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "createdBy"),
      onHeaderCell: onHeaderCell,
      hidden: !!bucketName,
      width: "9%",
      responsive: ["xxl"],
    },
    // {
    //   title: "Created At",
    //   key: "createdAt",
    //   render: (value, row) => (
    //     <s.Column isActive={row.is_active || row.leftDays < 0}>
    //       {getAccountTimeFormatDate(
    //         row.created_at,
    //         selectedAccount,
    //         true,
    //         false
    //       )}
    //     </s.Column>
    //   ),
    //   sorter: true,
    //   defaultSortOrder: "descend",
    //   sortOrder: changeSortOrderAntd(queryInfo, "createdAt"),
    //   onHeaderCell: onHeaderCell,
    //   hidden: !!bucketName,
    //   responsive: ["xl"],
    //   width: 120,
    // },
    {
      title: "Updated By",
      key: "updatedBy",
      render: (value, row) => (
        <s.Column isActive={row.is_active || row.leftDays < 0}>
          {row.updated_by ?? row.created_by}
        </s.Column>
      ),

      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "updatedBy"),
      onHeaderCell: onHeaderCell,
      hidden: !bucketName,
      responsive: ["md"],
      width: 150,
    },
    {
      title: "Updated At",
      key: "updatedAt",
      render: (value, row) => (
        <s.Column isActive={row.is_active || row.leftDays < 0}>
          {getAccountTimeFormatDate(
            row.updated_at ?? row.created_at,
            selectedAccount,
            true,
            false
          )}
        </s.Column>
      ),

      sorter: true,
      defaultSortOrder: "descend",
      sortOrder: changeSortOrderAntd(queryInfo, "updatedAt"),
      onHeaderCell: onHeaderCell,
      hidden: !bucketName,
      width: 180,
    },
    {
      title: "Activated",
      key: "activatedAt",
      render: (value, row) => {
        return (
          <s.Column isActive={row.is_active || row.leftDays < 0}>
            {row.activated_at ? `${row.activated_at.split(" ")[0]} UTC` : ""}
          </s.Column>
        );
      },
      responsive: ["xl"],
      width: 120,
      sorter: true,
      defaultSortOrder: "descend",
      sortOrder: changeSortOrderAntd(queryInfo, "activatedAt"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Expiration",
      key: "expiredAt",
      render: (value, row) => {
        return (
          <s.Column isActive={row.is_active || row.leftDays < 0}>
            {row.expired_at ? `${row.expired_at.split(" ")[0]} UTC` : ""}
          </s.Column>
        );
      },
      responsive: ["lg"],
      width: 120,
      sorter: true,
      defaultSortOrder: "descend",
      sortOrder: changeSortOrderAntd(queryInfo, "expiredAt"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Comment",
      key: "comment",
      render: (value, row) => {
        return (
          <s.Column isActive={row.is_active || row.leftDays < 0}>
            {row.comment}
          </s.Column>
        );
      },
      responsive: ["xxl"],
      width: 200,
    },
    {
      title: "",
      render: (value, row, index) => {
        return (
          <div className="table-button-wrapper">
            {!bucketName && (
              <TableButton
                onClick={(e) => {
                  e.stopPropagation();
                  queryNavigate({
                    to: `${location.pathname}/${row.bucket_name}`,
                    queryInfo: {
                      ...initialQueryinfo,
                      sortType: "updatedAt",
                    },
                  });
                }}
                label="History"
                data-testid={`subscription-lyve-history-${index}`}
              />
            )}

            <TableButton
              onClick={(e) => {
                e.stopPropagation();
                onModal("edit", row);
              }}
              label="Edit"
              data-testid={`subscription-lyve-edit-${index}`}
            />
          </div>
        );
      },
      width: !!bucketName ? (isMobile ? 40 : 80) : isMobile ? 80 : 180,
    },
  ];
  const mutationUpdateSize = useStatusMutation(updateLyveStorageSize, {
    onSuccess: (res: UpdateLyveStorageSizeResponse) => {
      if (res.error !== 0 || !res.result) {
        handleErrorNotify(
          createCustomError(res, updateErrorMsg),
          lang.label.lyve.notify.update.fail
        );
        return;
      }
      notifySuccess(lang.label.lyve.notify.update.success);

      setUpdatedKey(res.result);
      refetch();
    },
    onError: (error: CustomError) => {
      handleErrorNotify(error, updateErrorMsg, "label.lyve.notify.update.fail");
    },
  });

  const onChangePartNumber = (partNumber: string) => {
    if (!mutationUpdateSize.idle) return;
    if (!selectRow?.subscription_key_id) return;
    if (!selectRow?.is_active) return;
    if (partNumber === selectRow?.part_number) return;
    mutationUpdateSize.mutate({
      id: selectRow?.subscription_key_id,
      partNumber: partNumber,
    });
  };

  useEffect(() => {
    setButtons(
      <mediaQuery.Default>
        <RBACWrapper requiredPermissions={"generateCumulus"}>
          <BigButton
            label={"Generate Keys"}
            iconComponent={<Key />}
            onClickBtn={() => {
              onModal("add");
            }}
            data-testid="subscription-lyve-button-modal"
          />
        </RBACWrapper>
      </mediaQuery.Default>
    );
  }, [onModal, setButtons]);

  return (
    <LyvePresenter
      queryInfo={queryInfo}
      columns={columns}
      data={data}
      onChangePage={onChangePage}
      isLoading={isLoadingList || isLoadingHistory}
      isAddModal={isAddModal}
      isEditModal={isEditModal}
      onModal={onModal}
      selectRow={selectRow}
      refetch={refetch}
      onChangePartNumber={onChangePartNumber}
      updatedKey={updatedKey}
      type={type}
    />
  );
}
